// ImportScheduleModal — photo of a paper chit ledger → Gemini parse → per-row
// reconcile → one commit into the chit's payout schedule.
//
// Flow: capture (camera/file → base64 → chits.parse_ledger_photo) →
// reconcile (edit amounts; resolve each member as create-with-phone | link to
// an existing member via fuzzy search | unassigned) → commit
// (chits.import_cycle_plan, per-row results).
//
// Dedup: new members default to create-with-phone, and the phone-idempotent
// subscriber.create makes duplicates impossible; the fuzzy match surfaces an
// existing member to LINK instead of re-creating. The company row defaults to a
// "நிறுவனம் / Company" member.

import { useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc } from '../lib/orpc.js';
import { friendly_error } from '../lib/friendly-error.js';

const PHONE_RE = /^\+[1-9]\d{6,14}$/;
const LOW_CONFIDENCE = 0.75;

interface ChitLite {
  id: string;
  pool_size: number;
  duration_months: number;
  monthly_subscription_paise: number;
  collection_day: number;
}

interface Props {
  chit_id: string;
  chit: ChitLite;
  lang: string;
  on_close: () => void;
}

// Mirrors the chits.parse_ledger_photo output rows.
interface ParsedRow {
  cycle_index: number;
  date_iso: string | null;
  pay_amount_paise: number | null;
  receive_amount_paise: number | null;
  name_ta: string;
  name_en: string;
  phone: string | null;
  is_company: boolean;
  confidence: number;
}

type Resolution =
  | { kind: 'unassigned' }
  | { kind: 'existing'; subscriber_id: string; label: string }
  | { kind: 'create'; name_ta: string; name_en: string; phone: string };

interface ReconcileRow {
  cycle_index: number;
  date_iso: string | null;
  pay_rupees: string;
  receive_rupees: string;
  name_ta: string;
  name_en: string;
  phone: string | null; // parsed phone (E.164|null) — restored when switching to New mode
  is_company: boolean;
  confidence: number;
  resolution: Resolution;
  result?: { ok: boolean; error: string | null; slot_taken: boolean };
}

interface CommitRow {
  cycle_index: number;
  ok: boolean;
  error: string | null;
  slot_taken: { cycle_index: number; current_recipient_name: string } | null;
  subscriber_id: string | null;
  created_subscriber: boolean;
}

function paise_to_rupee_str(paise: number | null): string {
  return paise == null ? '' : String(Math.round(paise / 100));
}

function parsed_to_reconcile(r: ParsedRow): ReconcileRow {
  // Default everyone to create-with-phone (the common migration case); the
  // resolver surfaces fuzzy matches so the operator can LINK instead.
  return {
    cycle_index: r.cycle_index,
    date_iso: r.date_iso,
    pay_rupees: paise_to_rupee_str(r.pay_amount_paise),
    receive_rupees: paise_to_rupee_str(r.receive_amount_paise),
    name_ta: r.name_ta,
    name_en: r.name_en,
    phone: r.phone ?? null,
    is_company: r.is_company,
    confidence: r.confidence,
    // Company row = the foreman (self) → leave blank (still assignable). Robust to
    // Gemini missing the is_company flag: also treat a company-ish name as company.
    resolution:
      r.is_company || /company|கம்பேனி|நிறுவனம்/i.test(`${r.name_ta} ${r.name_en}`)
        ? { kind: 'unassigned' }
        : // Prefill the phone Gemini read off the ledger (already E.164). A row that
          // came with a phone is import-ready with no typing; phone-idempotent create
          // links it to an existing member if the number already exists.
          { kind: 'create', name_ta: r.name_ta, name_en: r.name_en, phone: r.phone ?? '' },
  };
}

function row_ready(r: ReconcileRow): boolean {
  if (r.resolution.kind === 'create') {
    return (
      r.resolution.name_ta.trim().length > 0 &&
      r.resolution.name_en.trim().length > 0 &&
      PHONE_RE.test(r.resolution.phone)
    );
  }
  if (r.resolution.kind === 'existing') return r.resolution.subscriber_id.length > 0;
  return true; // unassigned
}

// Best-effort member identity for proactive duplicate detection in the UI:
//   create → its phone (last 10 digits, format-insensitive)
//   existing → the linked subscriber_id
//   unassigned / incomplete-phone → no key (never flagged)
// Two rows sharing a key collapse to one member, who can hold only one slot per
// chit — so they can't all be imported. The backend's (chit_id, subscriber_id)
// uniqueness is the real guard; this turns that post-commit CONFLICT into an
// upfront, fixable warning. (Two valid formats of the same number share a key
// because we compare the trailing 10 digits, mirroring the server-side E.164
// canonicalization that makes one phone = one subscriber.)
function member_key(r: ReconcileRow): string | null {
  if (r.resolution.kind === 'existing') {
    return r.resolution.subscriber_id ? `id:${r.resolution.subscriber_id}` : null;
  }
  if (r.resolution.kind === 'create') {
    const digits = r.resolution.phone.replace(/\D/g, '');
    return digits.length >= 10 ? `ph:${digits.slice(-10)}` : null;
  }
  return null; // unassigned
}

async function read_as_base64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const data = reader.result;
      if (typeof data !== 'string') {
        reject(new Error('unexpected reader result'));
        return;
      }
      const comma = data.indexOf(',');
      resolve(comma >= 0 ? data.slice(comma + 1) : data);
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(file);
  });
}

export function ImportScheduleModal({ chit_id, chit, lang, on_close }: Props): ReactElement {
  const qc = useQueryClient();
  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);

  const [step, set_step] = useState<'capture' | 'reconcile' | 'result'>('capture');
  const [rows, set_rows] = useState<ReconcileRow[]>([]);
  const [warnings, set_warnings] = useState<string[]>([]);
  const [collection_day, set_collection_day] = useState<number | null>(null);
  const [dry_run, set_dry_run] = useState(false);
  const [replace_existing, set_replace_existing] = useState(false);

  const parse_m = useMutation({
    mutationFn: (image_base64: string) =>
      orpc.chits.parse_ledger_photo({
        chit_id,
        image_base64,
        pool_size: chit.pool_size,
        duration_months: chit.duration_months,
      }),
    onSuccess: (r) => {
      const parsed = r.rows as ParsedRow[];
      set_rows(parsed.map(parsed_to_reconcile));
      set_warnings(r.warnings ?? []);
      set_collection_day(r.collection_day ?? null);
      set_dry_run(Boolean(r.dry_run));
      set_step('reconcile');
    },
  });

  const commit_m = useMutation({
    mutationFn: () =>
      orpc.chits.import_cycle_plan({
        chit_id,
        replace_existing,
        batch_label: `imported ${chit.duration_months}-cycle ledger`,
        rows: rows.map((r) => ({
          cycle_index: r.cycle_index,
          pay_amount_paise: Math.round((Number(r.pay_rupees) || 0) * 100),
          receive_amount_paise: Math.round((Number(r.receive_rupees) || 0) * 100),
          recipient:
            r.resolution.kind === 'create'
              ? {
                  kind: 'create' as const,
                  name_ta: r.resolution.name_ta,
                  name_en: r.resolution.name_en,
                  phone: r.resolution.phone,
                }
              : r.resolution.kind === 'existing'
                ? { kind: 'existing' as const, subscriber_id: r.resolution.subscriber_id }
                : { kind: 'unassigned' as const },
        })),
      }),
    onSuccess: async (r) => {
      const results = r.results as CommitRow[];
      const by_cycle = new Map(results.map((x) => [x.cycle_index, x]));
      set_rows((prev) =>
        prev.map((row) => {
          const res = by_cycle.get(row.cycle_index);
          return res
            ? { ...row, result: { ok: res.ok, error: res.error, slot_taken: res.slot_taken != null } }
            : row;
        }),
      );
      set_step('result');
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['cycle-plan', chit_id] }),
        qc.invalidateQueries({ queryKey: ['memberships', chit_id] }),
        qc.invalidateQueries({ queryKey: ['ledger-matrix', chit_id] }),
      ]);
    },
  });

  const set_row = (cycle_index: number, patch: Partial<ReconcileRow>): void => {
    set_rows((prev) => prev.map((r) => (r.cycle_index === cycle_index ? { ...r, ...patch } : r)));
  };

  const unresolved = rows.filter((r) => !row_ready(r)).length;
  const low_conf = rows.filter((r) => r.confidence < LOW_CONFIDENCE).length;
  const any_slot_taken = rows.some((r) => r.result?.slot_taken);
  const ok_count = rows.filter((r) => r.result?.ok).length;

  // Proactive duplicate-member map: cycle_index → other cycle indices that
  // resolve to the SAME member. Any member assigned to >1 row can't be imported
  // (one slot per chit), so we flag the rows inline + gate Import before commit.
  const member_dups = new Map<number, number[]>();
  {
    const groups = new Map<string, number[]>();
    for (const r of rows) {
      const k = member_key(r);
      if (k) groups.set(k, [...(groups.get(k) ?? []), r.cycle_index]);
    }
    for (const cycles of groups.values()) {
      if (cycles.length > 1) {
        for (const c of cycles) member_dups.set(c, cycles.filter((x) => x !== c));
      }
    }
  }
  const dup_count = member_dups.size;
  // Any failed row → drives the fix banner + result-screen "Fix failed rows".
  const has_failures = rows.some((r) => r.result && !r.result.ok);
  // Failures that EDITING fixes (everything except slot-taken, which has its own
  // one-click "Retry with replace"). Splits the two failure modes cleanly.
  const has_editable_failures = rows.some((r) => r.result && !r.result.ok && !r.result.slot_taken);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm"
      onClick={on_close}
    >
      <div
        className="bg-surface border border-line rounded-lg w-full max-w-5xl p-6 shadow-lg max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-medium">
            {t('புகைப்படத்திலிருந்து அட்டவணை இறக்குமதி', 'Import schedule from photo')}
          </h2>
          <div className="flex items-center gap-2">
            {dry_run && (
              <span className="text-[11px] text-warn bg-warn-soft px-2 py-0.5 rounded">DRY RUN</span>
            )}
            <button
              type="button"
              onClick={on_close}
              aria-label={t('மூடு', 'Close')}
              className="text-ink/50 hover:text-ink text-xl leading-none px-1"
            >
              ✕
            </button>
          </div>
        </div>

        {/* ── Step 1: capture ── */}
        {step === 'capture' && (
          <div className="py-6">
            <p className="text-xs text-ink/60 mb-4">
              {t(
                'காகித லெட்ஜரின் புகைப்படத்தை எடுக்கவும் அல்லது பதிவேற்றவும். ஒவ்வொரு வரிசையும் (தேதி, கட்டணம், பெறும் தொகை, பெயர்) தானாகப் படிக்கப்படும்.',
                'Take or upload a photo of the paper ledger. Each row (date, pay, receive, name) is read automatically.',
              )}
            </p>
            <div className="flex items-center gap-2">
              <label
                className={`inline-flex items-center gap-2 px-4 py-2 rounded border border-line bg-bg-elev hover:bg-surface cursor-pointer text-sm ${
                  parse_m.isPending ? 'opacity-50 pointer-events-none' : ''
                }`}
              >
                📷 {parse_m.isPending ? t('படிக்கிறது…', 'Reading…') : t('புகைப்படம் தேர்வு', 'Choose photo')}
                <input
                  type="file"
                  accept="image/*"
                  // eslint-disable-next-line react/no-unknown-property — valid HTML attr, React types lag
                  {...({ capture: 'environment' } as Record<string, string>)}
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      void read_as_base64(f).then((b64) => parse_m.mutate(b64));
                    }
                    e.target.value = '';
                  }}
                />
              </label>
              {!parse_m.isPending && (
                <button
                  type="button"
                  onClick={on_close}
                  className="px-4 py-2 rounded border border-line text-ink/70 hover:bg-bg-elev text-sm"
                >
                  {t('ரத்து', 'Cancel')}
                </button>
              )}
            </div>
            {parse_m.isPending && (
              <div className="mt-3 text-xs text-ink/60">
                {t('படத்தைப் படிக்கிறது… இதற்கு 20-30 வினாடிகள் ஆகலாம்.', 'Reading the photo… this can take 20–30 seconds.')}
              </div>
            )}
            {parse_m.error && (
              <div className="mt-3 px-3 py-2 bg-bad-soft text-bad text-xs rounded">
                {friendly_error(parse_m.error)}
              </div>
            )}
          </div>
        )}

        {/* ── Step 2: reconcile ── */}
        {step === 'reconcile' && (
          <div>
            <div className="flex flex-wrap items-center gap-3 text-xs mb-3">
              <span className="text-ink/70">
                {rows.length} {t('வரிசைகள்', 'rows')}
              </span>
              {collection_day != null && (
                <span className="text-ink/60">
                  {t('கடைசி நாள்', 'Collection day')}: <b>{collection_day}</b>
                </span>
              )}
              {low_conf > 0 && (
                <span className="text-warn">
                  ⚠ {low_conf} {t('வரிசைகள் சரிபார்க்கவும்', 'rows to verify')}
                </span>
              )}
              {unresolved > 0 && (
                <span className="text-bad">
                  {unresolved} {t('தீர்க்கப்படாதது', 'need a phone / link')}
                </span>
              )}
              {dup_count > 0 && (
                <span className="text-bad font-medium">
                  ⚠ {dup_count} {t('நகல் உறுப்பினர்', 'duplicate member')}
                </span>
              )}
            </div>

            {has_failures && (
              <div className="mb-3 px-3 py-2 bg-bad-soft text-bad text-[11px] rounded">
                {t(
                  'சில வரிசைகள் தோல்வியடைந்தன — கீழே குறிக்கப்பட்ட வரிசைகளைச் சரிசெய்து மீண்டும் இறக்குமதி செய்யவும். ஏற்கனவே சேமித்தவை மீண்டும் சேமிக்கப்படா.',
                  'Some rows failed — fix the highlighted rows below and import again. Already-saved rows are re-affirmed, not duplicated.',
                )}
              </div>
            )}

            {warnings.length > 0 && (
              <div className="mb-3 px-3 py-2 bg-warn-soft text-warn text-[11px] rounded space-y-0.5">
                {warnings.map((w, i) => (
                  <div key={i}>• {w}</div>
                ))}
              </div>
            )}

            <div className="border border-line rounded overflow-x-auto max-h-[55vh] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-bg-elev border-b border-line text-ink/60 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1.5 w-8">#</th>
                    <th className="text-left px-2 py-1.5 w-24">{t('தேதி', 'Date')}</th>
                    <th className="text-right px-2 py-1.5 w-24">{t('கட்டணம்', 'Pay ₹')}</th>
                    <th className="text-right px-2 py-1.5 w-24">{t('பெறும்', 'Receive ₹')}</th>
                    <th className="text-left px-2 py-1.5">{t('பெறுநர்', 'Assigned to')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const low = r.confidence < LOW_CONFIDENCE;
                    const failed = Boolean(r.result && !r.result.ok);
                    const imported = Boolean(r.result?.ok);
                    const dups = member_dups.get(r.cycle_index);
                    const row_bg = failed
                      ? 'bg-bad-soft/40'
                      : dups
                        ? 'bg-warn-soft/60'
                        : low
                          ? 'bg-warn-soft/40'
                          : '';
                    return (
                      <tr
                        key={r.cycle_index}
                        className={`border-b border-line/40 last:border-b-0 ${row_bg}`}
                      >
                        <td className="px-2 py-1.5 font-mono align-top">{r.cycle_index}</td>
                        <td className="px-2 py-1.5 font-mono align-top text-ink/70">{r.date_iso ?? '—'}</td>
                        <td className="px-2 py-1.5 align-top">
                          <input
                            inputMode="numeric"
                            value={r.pay_rupees}
                            onChange={(e) => set_row(r.cycle_index, { pay_rupees: e.target.value })}
                            className="w-20 px-1 py-0.5 border border-line rounded text-right font-mono"
                          />
                        </td>
                        <td className="px-2 py-1.5 align-top">
                          <input
                            inputMode="numeric"
                            value={r.receive_rupees}
                            onChange={(e) => set_row(r.cycle_index, { receive_rupees: e.target.value })}
                            className="w-20 px-1 py-0.5 border border-line rounded text-right font-mono"
                          />
                        </td>
                        <td className="px-2 py-1.5 align-top">
                          <RecipientResolver
                            row={r}
                            lang={lang}
                            // Editing a row's recipient invalidates its last commit
                            // outcome — drop the stale ✗/✓ so the fix shows clean
                            // until the next import re-affirms it.
                            on_change={(resolution) =>
                              set_row(r.cycle_index, { resolution, result: undefined })
                            }
                          />
                          {dups && (
                            <div className="mt-1 text-[11px] text-bad">
                              ⚠ {t('அதே உறுப்பினர் வரிசை', 'same member as row')} #{dups.join(', #')} —{' '}
                              {t('ஒரு சிட்டில் ஒரு இடம் மட்டுமே', 'only one slot per chit')}
                            </div>
                          )}
                          {failed && (
                            <div className="mt-1 text-[11px] text-bad">
                              ✗ {r.result?.error ?? t('தோல்வி', 'failed')}
                            </div>
                          )}
                          {imported && (
                            <div className="mt-1 text-[11px] text-good">
                              ✓ {t('இறக்குமதி ஆனது', 'imported')}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {commit_m.error && (
              <div className="mt-3 px-3 py-2 bg-bad-soft text-bad text-xs rounded">
                {friendly_error(commit_m.error)}
              </div>
            )}

            <div className="flex items-center gap-2 justify-end mt-4">
              <button
                onClick={on_close}
                className="px-4 py-2 rounded border border-line text-ink/70 hover:bg-bg-elev text-sm"
              >
                {t('ரத்து', 'Cancel')}
              </button>
              <button
                type="button"
                disabled={commit_m.isPending || unresolved > 0 || dup_count > 0}
                onClick={() => commit_m.mutate()}
                className="bg-primary hover:bg-primary-2 disabled:bg-ink/20 text-white px-4 py-2 rounded text-sm font-medium"
                title={
                  dup_count > 0
                    ? t('நகல் உறுப்பினர்களை சரிசெய்யவும்', 'Resolve duplicate members first')
                    : unresolved > 0
                      ? t('எல்லா வரிசைகளையும் தீர்க்கவும்', 'Resolve every row first')
                      : undefined
                }
              >
                {commit_m.isPending
                  ? '…'
                  : t(`${rows.length} வரிசைகளை இறக்குமதி செய்`, `Import ${rows.length} rows`)}
              </button>
            </div>
          </div>
        )}

        {/* ── Step 3: result ── */}
        {step === 'result' && (
          <div>
            <div className="flex items-baseline gap-3 text-sm mb-3">
              <span className="font-medium text-good">
                {ok_count}/{rows.length} {t('சேமிக்கப்பட்டது', 'committed')}
              </span>
              {ok_count < rows.length && (
                <span className="text-bad">
                  {rows.length - ok_count} {t('தோல்வி', 'failed')}
                </span>
              )}
            </div>
            <div className="border border-line rounded max-h-[55vh] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-bg-elev border-b border-line text-ink/60 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-1.5 w-8">#</th>
                    <th className="text-left px-3 py-1.5 w-40">{t('பெயர்', 'Name')}</th>
                    <th className="text-left px-3 py-1.5">{t('நிலை', 'Status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.cycle_index} className="border-b border-line/40 last:border-b-0">
                      <td className="px-3 py-1.5 font-mono">{r.cycle_index}</td>
                      <td className="px-3 py-1.5">{lang === 'ta' ? r.name_ta : r.name_en}</td>
                      <td className="px-3 py-1.5">
                        {r.result?.ok ? (
                          <span className="text-good">✓ {t('சரி', 'ok')}</span>
                        ) : (
                          <span className="text-bad">✗ {r.result?.error ?? t('தோல்வி', 'failed')}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-2 justify-end mt-4">
              {has_editable_failures && (
                <button
                  type="button"
                  onClick={() => set_step('reconcile')}
                  className="px-4 py-2 rounded border border-line text-ink/70 hover:bg-bg-elev text-sm"
                >
                  {t('தோல்வியடைந்தவற்றைச் சரிசெய்', 'Fix failed rows')}
                </button>
              )}
              {any_slot_taken && (
                <button
                  type="button"
                  disabled={commit_m.isPending}
                  onClick={() => {
                    set_replace_existing(true);
                    set_step('reconcile');
                  }}
                  className="px-4 py-2 rounded border border-line text-ink/70 hover:bg-bg-elev text-sm"
                >
                  {t('மாற்றியமைத்து மீண்டும் முயற்சி', 'Retry with replace')}
                </button>
              )}
              <button
                onClick={on_close}
                className="bg-primary hover:bg-primary-2 text-white px-4 py-2 rounded text-sm font-medium"
              >
                {t('முடிந்தது', 'Done')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Per-row recipient resolver. Explicit 3-way choice so it's never ambiguous:
//   New (create with phone) · Existing (search + link a current member) · Skip.
// "Existing" is a real lookup over ALL tenant members — not just the auto-match —
// so an operator can link someone the fuzzy match didn't surface.
function RecipientResolver({
  row,
  lang,
  on_change,
}: {
  row: ReconcileRow;
  lang: string;
  on_change: (r: Resolution) => void;
}): ReactElement {
  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);
  const res = row.resolution;
  const mode: 'new' | 'existing' | 'skip' =
    res.kind === 'create' ? 'new' : res.kind === 'existing' ? 'existing' : 'skip';
  const selected = res.kind === 'existing' && res.subscriber_id ? res : null;

  // Existing-mode search box; pre-seed with the parsed name so it auto-finds the
  // likely member, but the operator can type anything to look up someone else.
  const [q, set_q] = useState(row.name_en || row.name_ta);

  // New mode → quiet auto-suggest by the parsed name; Existing (unselected) →
  // live search by the typed query. One query, term depends on mode.
  const term =
    mode === 'existing' && !selected
      ? q.trim()
      : mode === 'new' && res.kind === 'create'
        ? (res.name_en || res.name_ta).trim()
        : '';
  const search = useQuery({
    queryKey: ['import-search', term],
    queryFn: () => orpc.reports.subscriber_search({ q: term }),
    enabled: term.length >= 2,
  });
  const matches = (search.data?.subscribers ?? []) as Array<{
    id: string;
    name_ta: string;
    name_en: string;
    phone: string;
  }>;

  const pick = (m: { id: string; name_ta: string; name_en: string; phone: string }): void =>
    on_change({ kind: 'existing', subscriber_id: m.id, label: `${m.name_en || m.name_ta} · ${m.phone}` });
  const pill = (active: boolean): string =>
    `text-[11px] px-2 py-0.5 rounded-full border ${
      active ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-line text-ink/55 hover:bg-bg-elev'
    }`;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1">
        <button type="button" className={pill(mode === 'new')}
          onClick={() => on_change({ kind: 'create', name_ta: row.name_ta, name_en: row.name_en, phone: row.phone ?? '' })}>
          {t('புதியது', 'New')}
        </button>
        <button type="button" className={pill(mode === 'existing')}
          onClick={() => on_change({ kind: 'existing', subscriber_id: '', label: '' })}>
          {t('ஏற்கனவே', 'Existing')}
        </button>
        <button type="button" className={pill(mode === 'skip')}
          onClick={() => on_change({ kind: 'unassigned' })}>
          {t('தவிர்', 'Skip')}
        </button>
      </div>

      {mode === 'new' && res.kind === 'create' && (
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <input value={res.name_en} onChange={(e) => on_change({ ...res, name_en: e.target.value })}
              placeholder="Name (EN)" className="w-28 px-1 py-0.5 border border-line rounded" />
            <input value={res.name_ta} onChange={(e) => on_change({ ...res, name_ta: e.target.value })}
              placeholder="பெயர்" className="w-24 px-1 py-0.5 border border-line rounded" />
            <input value={res.phone} inputMode="tel"
              onChange={(e) => on_change({ ...res, phone: e.target.value.replace(/[^\d+]/g, '') })}
              placeholder="+91…"
              className={`w-28 px-1 py-0.5 border rounded font-mono ${res.phone.length > 0 && !PHONE_RE.test(res.phone) ? 'border-bad' : 'border-line'}`} />
          </div>
          {matches.length > 0 && (
            <div className="text-[11px] text-ink/55">
              {t('ஏற்கனவே உறுப்பினரா?', 'Looks like an existing member:')}{' '}
              <button type="button" className="underline text-primary" onClick={() => pick(matches[0]!)}>
                🔗 {matches[0]!.name_en || matches[0]!.name_ta} · {matches[0]!.phone}
              </button>
            </div>
          )}
        </div>
      )}

      {mode === 'existing' &&
        (selected ? (
          <div className="flex items-center gap-2">
            <span className="text-good">🔗 {selected.label}</span>
            <button type="button" className="text-[11px] text-ink/60 underline"
              onClick={() => on_change({ kind: 'existing', subscriber_id: '', label: '' })}>
              {t('மாற்று', 'change')}
            </button>
          </div>
        ) : (
          <div className="space-y-1">
            <input autoFocus value={q} onChange={(e) => set_q(e.target.value)}
              placeholder={t('உறுப்பினரைத் தேடு…', 'Search members…')}
              className="w-64 px-2 py-0.5 border border-line rounded" />
            {term.length >= 2 && (
              <div className="border border-line rounded max-h-28 overflow-y-auto bg-surface">
                {matches.length === 0 ? (
                  <div className="px-2 py-1 text-[11px] text-ink/50">
                    {search.isFetching ? t('தேடுகிறது…', 'searching…') : t('உறுப்பினர் இல்லை', 'no members found')}
                  </div>
                ) : (
                  matches.slice(0, 8).map((m) => (
                    <button key={m.id} type="button" onClick={() => pick(m)}
                      className="block w-full text-left px-2 py-1 text-[11px] hover:bg-bg-elev">
                      {m.name_en || m.name_ta} · <span className="font-mono text-ink/60">{m.phone}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        ))}

      {mode === 'skip' && (
        <span className="text-[11px] text-ink/50">{t('இந்த வரிசை ஒதுக்கப்படாது', '— not assigned —')}</span>
      )}
    </div>
  );
}
